import { useState } from 'preact/hooks'
import { RefreshCcw, Plus, AlertCircle, AtSign, Star, Trash2, Loader2 } from 'lucide-preact'
import { useReplyToAddresses } from '@/hooks/useReplyToAddresses'
import { useToast } from '@/hooks/useToast'

interface ReplyToAddressesListProps {
  clientCompanyId: string
}

// Format-only guard; the server is authoritative on uniqueness, the single-default invariant, and the cap.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ReplyToAddressesList({ clientCompanyId }: ReplyToAddressesListProps) {
  const { addresses, loading, saving, error, load, addAddress, removeAddress, setDefault } =
    useReplyToAddresses(clientCompanyId)
  const { showError, showSuccess } = useToast()
  // Tracks which row is mid-mutation so that row's action button shows a spinner. All rows' actions are
  // disabled during a save (disabled={saving}) to prevent overlapping replace-whole-list writes.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  // Inline add form — a single email field + optional "make default" is too small to warrant an overlay.
  const [showAddForm, setShowAddForm] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newMakeDefault, setNewMakeDefault] = useState(false)
  const [submittingAdd, setSubmittingAdd] = useState(false)

  const hasAddresses = !!(addresses && addresses.length > 0)
  const isNewEmailValid = EMAIL_PATTERN.test(newEmail.trim())

  const openAddForm = () => {
    setNewEmail('')
    setNewMakeDefault(false)
    setShowAddForm(true)
  }

  const closeAddForm = () => {
    setShowAddForm(false)
    setNewEmail('')
    setNewMakeDefault(false)
  }

  const handleAdd = async () => {
    const trimmed = newEmail.trim()
    if (!EMAIL_PATTERN.test(trimmed)) {
      showError('Invalid email', 'Enter a valid email address.')
      return
    }
    setSubmittingAdd(true)
    const result = await addAddress(trimmed, newMakeDefault)
    setSubmittingAdd(false)
    if (!result.ok) {
      if (!result.handled) showError('Could not add address', result.error || 'Unknown error')
      return
    }
    showSuccess('Address added', `${trimmed} was added to the reply-to book.`)
    closeAddForm()
  }

  const handleSetDefault = async (email: string) => {
    setPendingEmail(email)
    const result = await setDefault(email)
    setPendingEmail(null)
    if (!result.ok) {
      if (!result.handled) showError('Could not set default', result.error || 'Unknown error')
      return
    }
    showSuccess('Default updated', `${email} is now the default reply-to address.`)
  }

  const handleRemove = async (email: string) => {
    setPendingEmail(email)
    const result = await removeAddress(email)
    setPendingEmail(null)
    if (!result.ok) {
      if (!result.handled) showError('Could not remove address', result.error || 'Unknown error')
      return
    }
    showSuccess('Address removed', `${email} was removed.`)
  }

  return (
    <div className="px-6 py-4 max-w-7xl mx-auto space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Reply-To Addresses</h2>
          <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 max-w-prose">
            Manage the addresses your marketing email replies go to, then select one on each email activation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Refreshing' : 'Refresh'}
          </button>
          {!showAddForm && (
            <button
              onClick={openAddForm}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add address
            </button>
          )}
        </div>
      </div>

      {/* Inline add form — replaces the old full-screen panel for a single field */}
      {showAddForm && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0 space-y-1">
              <label htmlFor="new-reply-to-email" className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                Email address
              </label>
              <input
                id="new-reply-to-email"
                type="email"
                autoFocus
                value={newEmail}
                onInput={e => setNewEmail((e.target as HTMLInputElement).value)}
                onKeyDown={e => { if (e.key === 'Enter' && isNewEmailValid && !submittingAdd) { void handleAdd() } }}
                placeholder="replies@yourbrand.com"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={closeAddForm}
                className="px-3 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleAdd() }}
                disabled={!isNewEmailValid || submittingAdd}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {submittingAdd && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
          {newEmail.trim() && !isNewEmailValid && (
            <p className="text-xs text-red-600 dark:text-red-400">Enter a valid email address.</p>
          )}
          {hasAddresses ? (
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={newMakeDefault}
                onChange={e => setNewMakeDefault((e.target as HTMLInputElement).checked)}
                className="rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
              />
              Make this the default reply-to address
            </label>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">The first address you add becomes the default.</p>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && !addresses && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !hasAddresses && !error && !showAddForm && (
        <div className="p-8 border border-dashed rounded-xl border-slate-300 dark:border-slate-600 text-center">
          <AtSign className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No reply-to addresses yet. Add one to get started.</p>
          <button onClick={openAddForm} className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
            <Plus className="w-4 h-4" /> Add address
          </button>
        </div>
      )}

      {hasAddresses && (
        <ul className="space-y-3">
          {addresses!.map(address => {
            const isPending = saving && pendingEmail === address.Email
            return (
              <li
                key={address.Email}
                className="flex items-center justify-between gap-3 rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 px-5 py-4 shadow-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <AtSign className="w-4 h-4 shrink-0 text-slate-400" />
                  <span className="font-medium text-slate-900 dark:text-white break-all">{address.Email}</span>
                  {address.IsDefault && (
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 text-xs font-medium">
                      <Star className="w-3 h-3" /> Default
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!address.IsDefault && (
                    <button
                      type="button"
                      onClick={() => { void handleSetDefault(address.Email) }}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                    >
                      {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />} Set default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { void handleRemove(address.Email) }}
                    disabled={saving}
                    aria-label={`Remove ${address.Email}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                  >
                    {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Remove
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
